import { E as EmblaCarousel } from "./embla-carousel.esm-3bCdUmQG.js";
import { A as Autoplay } from "./embla-carousel-autoplay.esm-lYa57aZD.js";
import { d as debounce, i as isMobile, l as lazyInit } from "./utilities-2Suih3Nm.js";
import { a as addPrevNextBtnsClickHandlersSSR, b as addDotsBtnsAndClickHandlersSSR, c as addInstagramDotBtnsAndClickHandlers } from "./embla-extension-OubijMX1.js";
class ProductListing extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.cleanupFunctions = [];
    this.currentDeviceType = null;
    this._isFirstInit = true;
    this.nodes = {
      embla: null,
      viewport: null,
      container: null,
      prevBtn: null,
      nextBtn: null,
      dots: null
    };
    this.config = {
      showNavigation: false,
      showDots: false,
      options: {},
      autoPlay: {
        desktop: false,
        mobile: false
      }
    };
    this.handleResize = debounce(() => this.onResize(), 300);
  }
  connectedCallback() {
    this.cacheNodes();
    this.parseConfig();
    this.currentDeviceType = isMobile() ? "mobile" : "desktop";
    if (Shopify.designMode) {
      this.init();
    } else {
      lazyInit(this, () => this.init());
    }
    window.addEventListener("resize", this.handleResize);
  }
  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    if (!this.nodes.embla) return;
    this.nodes.viewport = this.nodes.embla.querySelector(".embla__viewport");
    this.nodes.container = this.nodes.embla.querySelector(".embla__container");
    this.nodes.prevBtn = this.nodes.embla.querySelector(".embla__snip-button--prev");
    this.nodes.nextBtn = this.nodes.embla.querySelector(".embla__snip-button--next");
    this.nodes.dots = this.nodes.embla.querySelector(":scope > .embla__snip-dots");
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showDots = this.dataset.showDots === "true";
    this.config.options = this.parseJSON(this.dataset.options, {});
    const autoPlayDesktop = this.parseJSON(this.dataset.autoPlayDesktop, false);
    const autoPlayMobile = this.parseJSON(this.dataset.autoPlayMobile, false);
    this.config.autoPlay.desktop = autoPlayDesktop || false;
    this.config.autoPlay.mobile = autoPlayMobile || autoPlayDesktop;
  }
  parseJSON(jsonString, fallback) {
    if (!jsonString || !jsonString.trim()) return fallback;
    try {
      const parsed = JSON.parse(jsonString.replace(/,\s*([}\]])/g, "$1"));
      if (typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
      return fallback;
    } catch (error) {
      return fallback;
    }
  }
  getCurrentAutoPlayConfig() {
    return isMobile() ? this.config.autoPlay.mobile : this.config.autoPlay.desktop;
  }
  // ========== Initialization ==========
  init() {
    this.destroy();
    this.initEmbla();
  }
  initEmbla() {
    if (!this.nodes.viewport) return;
    const autoPlayConfig = this.getCurrentAutoPlayConfig();
    const plugins = [];
    if (autoPlayConfig) {
      plugins.push(
        Autoplay({
          delay: autoPlayConfig.delay ?? 5e3,
          stopOnInteraction: autoPlayConfig.stopOnInteraction ?? false,
          stopOnMouseEnter: autoPlayConfig.stopOnMouseEnter ?? true,
          stopOnFocusIn: false
        })
      );
    }
    try {
      this.emblaApi = EmblaCarousel(this.nodes.viewport, this.config.options, plugins);
      this.setupEmblaFeatures();
      this._isFirstInit = false;
    } catch (error) {
    }
  }
  setupEmblaFeatures() {
    const isActive = this.emblaApi.internalEngine().options.active;
    const snapCount = this.emblaApi.scrollSnapList().length;
    if (this.nodes.dots) this.nodes.dots.style.display = "";
    if (this.nodes.prevBtn) this.nodes.prevBtn.style.display = "";
    if (this.nodes.nextBtn) this.nodes.nextBtn.style.display = "";
    if (!isActive || snapCount <= 1) {
      if (this.nodes.dots) this.nodes.dots.style.display = "none";
      if (this.nodes.prevBtn) this.nodes.prevBtn.style.display = "none";
      if (this.nodes.nextBtn) this.nodes.nextBtn.style.display = "none";
      return;
    }
    if (this.config.showNavigation) this.enableNavigation();
    if (this.config.showDots) this.enableDots();
  }
  enableNavigation() {
    const { prevBtn, nextBtn } = this.nodes;
    if (!prevBtn || !nextBtn || !this.emblaApi) return;
    const cleanup = addPrevNextBtnsClickHandlersSSR(this.emblaApi, prevBtn, nextBtn);
    this.registerCleanup(cleanup);
  }
  enableDots() {
    const { dots } = this.nodes;
    if (!dots || !this.emblaApi) return;
    const dotsInner = dots.querySelector(".embla__dots-inner") || dots;
    const ssrDotCount = dotsInner.querySelectorAll(".embla__dot").length;
    const snapCount = this.emblaApi.scrollSnapList().length;
    let cleanup;
    if (this._isFirstInit && ssrDotCount === snapCount) {
      cleanup = addDotsBtnsAndClickHandlersSSR(this.emblaApi, dots);
    } else {
      cleanup = addInstagramDotBtnsAndClickHandlers(this.emblaApi, dotsInner);
    }
    this.registerCleanup(cleanup);
  }
  // ========== Public API (Theme Editor) ==========
  scrollToSlide(slideIndex) {
    var _a;
    (_a = this.emblaApi) == null ? void 0 : _a.scrollTo(slideIndex);
  }
  // ========== Resize ==========
  onResize() {
    const newDevice = isMobile() ? "mobile" : "desktop";
    if (this.currentDeviceType !== newDevice) {
      this.currentDeviceType = newDevice;
      this.init();
    }
  }
  // ========== Cleanup ==========
  registerCleanup(fn) {
    if (fn && typeof fn === "function") this.cleanupFunctions.push(fn);
  }
  destroy() {
    this.cleanupFunctions.forEach((fn) => {
      try {
        fn();
      } catch {
      }
    });
    this.cleanupFunctions = [];
    if (this.emblaApi) {
      try {
        this.emblaApi.destroy();
      } catch {
      }
      this.emblaApi = null;
    }
  }
}
if (!customElements.get("product-listing-component")) {
  customElements.define("product-listing-component", ProductListing);
}
